import { useEffect } from 'react';
import { useControls } from 'leva';
import type { CameraViewOffsetConfig } from '../../shared/appConfigTypes';
import { $appConfig, updateCameraConfig } from '../../state/appConfig';

type ChangeContext = { fromPanel?: boolean };
type Edge = 'top' | 'right' | 'bottom' | 'left';

const $viewOffset = $appConfig.map((config) => config.camera.viewOffset);

const write = (patch: Partial<CameraViewOffsetConfig>): void => {
  updateCameraConfig({ viewOffset: { ...$viewOffset.getState(), ...patch } });
};

// `set` mirrors the chat panel's own writes into the sliders; only a panel edit goes back.
const onPanelEdit =
  <T>(apply: (value: T) => void) =>
  (value: T, _path: string, ctx: ChangeContext): void => {
    if (ctx.fromPanel) apply(value);
  };

const edge = (key: Edge, value: number) => ({
  value,
  min: 0,
  max: 2000,
  step: 1,
  onChange: onPanelEdit((next: number) => write({ [key]: next })),
});

/**
 * "View Offset" — the camera's covered-edge padding by hand (legacy AppConfigPanel
 * parity). The chat panel writes the same field on every height change, so the
 * sliders follow the store and a manual value lasts until the panel next moves.
 */
export const useViewOffsetControls = (): void => {
  const [, set] = useControls(
    'View Offset',
    () => {
      const offset = $viewOffset.getState();
      return {
        Enabled: {
          value: offset.enabled,
          onChange: onPanelEdit((enabled: boolean) => write({ enabled })),
        },
        Top: edge('top', offset.top),
        Right: edge('right', offset.right),
        Bottom: edge('bottom', offset.bottom),
        Left: edge('left', offset.left),
      };
    },
    [],
  );

  useEffect(
    () =>
      $viewOffset.watch(({ enabled, top, right, bottom, left }) =>
        set({ Enabled: enabled, Top: top, Right: right, Bottom: bottom, Left: left }),
      ),
    [set],
  );
};
